'use strict'

import * as ipAddress from 'ip-address'
import { BigInteger } from 'jsbn'

// Fork of https://github.com/miketmoore/ip-cidr
export class IPCIDR {
  private cidr: any
  private ipAddressType: any
  public address: any
  private addressStart: any
  private addressEnd: any
  private _isValid = false
  constructor(cidr) {
    const ipAddressType = cidr.match(':')
      ? ipAddress.Address6
      : ipAddress.Address4
    const address = new ipAddressType(cidr)

    this._isValid = address.isValid()

    if (this._isValid) {
      this.cidr = cidr
      this.ipAddressType = ipAddressType
      this.address = address
      this.addressStart = address.startAddress()
      this.addressEnd = address.endAddress()
    }
  }

  public isValid() {
    return this._isValid
  }

  public formatIP(address, options: any = {}) {
    if (options.type === 'bigInteger') {
      return address.bigInteger()
    } else if (options.type === 'addressObject') {
      return address
    }

    return address.addressMinusSuffix
  }

  public contains(address) {
    if (
      !(address instanceof ipAddress.Address6) &&
      !(address instanceof ipAddress.Address4)
    ) {
      // eslint-disable-next-line unicorn/prefer-ternary
      if (typeof address === 'object') {
        address = this.ipAddressType.fromBigInteger(address)
      } else {
        address = new this.ipAddressType(address)
      }
    }

    return address.isInSubnet(this.address)
  }

  public start(options: any = {}) {
    return this.formatIP(this.addressStart, options)
  }

  public end(options: any = {}) {
    return this.formatIP(this.addressEnd, options)
  }

  public toString() {
    return this.cidr
  }

  public toRange(options: any = {}) {
    return [
      this.formatIP(this.addressStart, options),
      this.formatIP(this.addressEnd, options)
    ]
  }

  public toObject(options: any = {}) {
    return {
      start: this.formatIP(this.addressStart, options),
      end: this.formatIP(this.addressEnd, options)
    }
  }

  public toArray(options?: any, results?: any) {
    // eslint-disable-next-line prefer-const
    let list: any[] = []
    // eslint-disable-next-line prefer-const
    let start = this.addressStart.bigInteger()
    // eslint-disable-next-line prefer-const
    let end = this.addressEnd.bigInteger()
    // eslint-disable-next-line prefer-const
    let length = end.subtract(start).add(new BigInteger('1'))
    // eslint-disable-next-line prefer-const
    let info = this.getChunkInfo(length, options)

    if (results) {
      Object.assign(results, info)
    }

    for (let i = info.from; i < info.to; i++) {
      // eslint-disable-next-line prefer-const
      let num = start.add(new BigInteger(i + ''))
      // eslint-disable-next-line prefer-const
      let ip = this.formatIP(this.ipAddressType.fromBigInteger(num), options)

      list.push(ip)
    }

    return list
  }

  public loop(fn: any, options?: any, results?: any) {
    options = options || {}

    // eslint-disable-next-line prefer-const
    let promise: Array<Promise<any>> = []
    // eslint-disable-next-line prefer-const
    let start = this.addressStart.bigInteger()
    // eslint-disable-next-line prefer-const
    let end = this.addressEnd.bigInteger()
    // eslint-disable-next-line prefer-const
    let length = end.subtract(start).add(new BigInteger('1'))
    // eslint-disable-next-line prefer-const
    let info = this.getChunkInfo(length, options)

    if (results) {
      Object.assign(results, info)
    }

    for (let i = info.from; i < info.to; i++) {
      // eslint-disable-next-line prefer-const
      let num = start.add(new BigInteger(i + ''))
      // eslint-disable-next-line prefer-const
      let ip = this.formatIP(this.ipAddressType.fromBigInteger(num), options)

      promise.push(fn(ip))
    }

    return Promise.all(promise)
  }

  public getChunkInfo(length, options) {
    let from, limit, to, maxLength

    if (!!options && options.from !== undefined) {
      if (typeof options.from !== 'object') {
        from = new BigInteger(options.from + '')
      }
    } else {
      from = new BigInteger('0')
    }

    if (!!options && options.limit !== undefined) {
      if (typeof options.limit !== 'object') {
        limit = new BigInteger(options.limit + '')
      }
    } else {
      limit = length
    }

    // eslint-disable-next-line prefer-const
    maxLength = length.subtract(from)

    if (limit.compareTo(maxLength) > 0) {
      limit = maxLength
    }

    // eslint-disable-next-line prefer-const
    to = from.add(limit)

    return {
      // eslint-disable-next-line object-shorthand
      from: from,
      // eslint-disable-next-line object-shorthand
      to: to,
      // eslint-disable-next-line object-shorthand
      limit: limit,
      // eslint-disable-next-line object-shorthand
      length: length
    }
  }
}
